// payStep.tsx - Corrected version with per_page support
import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import HeaderContent from "./headerContent";
import { fetchDetailListClubUsers } from "@/api/merchant/request.get";
import { useParams, useSearchParams } from "react-router";
import BodyContent from "./bodyContent";

const Content = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [enabled, setEnabled] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [per_page, setPer_page] = useState(10);

  // Initialize per_page from searchParams if available
  useEffect(() => {
    const perPageFromParams = Number(searchParams.get("per_page"));
    if (!isNaN(perPageFromParams) && perPageFromParams > 0 && [10, 25, 50, 100].includes(perPageFromParams)) {
      setPer_page(perPageFromParams);
    }
  }, [searchParams]);

  // Reset page to 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Reset page to 1 when per_page changes
  useEffect(() => {
    setPage(1);
  }, [per_page]);

  // Enable query when id, search, page, or per_page changes
  useEffect(() => {
    setEnabled(true);
  }, [id, search, page, per_page]); // Added per_page to dependencies

  // Initialize page from searchParams if available
  useEffect(() => {
    const pageFromParams = Number(searchParams.get("page"));
    if (!isNaN(pageFromParams) && pageFromParams > 0) {
      setPage(pageFromParams);
    }
  }, [searchParams]);

  const { data, isLoading, isFetching, isSuccess, refetch } = useQuery({
    queryKey: ["listClubsUsers-data", id, search, page, per_page], // Added per_page to queryKey
    queryFn: () => fetchDetailListClubUsers(id, search, page, per_page),
    enabled: enabled && !!id,
  });

  // Disable query state after successful fetch and data is available
  useEffect(() => {
    if (isSuccess && data) {
      setEnabled(false);
    }
  }, [isSuccess, data]);

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
    // Reset page to 1 when search changes and update URL params
    setPage(1);
    const currentSearchParams = new URLSearchParams(searchParams);
    currentSearchParams.delete("page");
    setSearchParams(currentSearchParams);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Update URL with new page number
    const currentSearchParams = new URLSearchParams(searchParams);
    currentSearchParams.set("page", String(newPage));
    setSearchParams(currentSearchParams);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPer_page(newPerPage);
    // Reset page to 1 when changing items per page
    setPage(1);
    // Update URL with new per_page value and reset page
    const currentSearchParams = new URLSearchParams(searchParams);
    currentSearchParams.set("per_page", String(newPerPage));
    currentSearchParams.delete("page"); // Reset page in URL
    setSearchParams(currentSearchParams);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <HeaderContent data={data} id={id} setEnabled={setEnabled} />
      </Grid>
      <Grid size={12} sx={{ padding: "0" }}>
        <BodyContent 
          data={data} 
          setEnabled={setEnabled} 
          refetch={refetch}
          search={search}
          onSearchChange={handleSearch}
          isLoading={isLoading || isFetching}
          page={page} 
          setPage={handlePageChange}
          perPage={per_page}
          onPerPageChange={handlePerPageChange}  // Pass the handler to BodyContent
        />
      </Grid>
    </Grid>
  );
};

export default Content;